import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Headphones,
  FileText,
  Sparkles,
  CheckCircle2,
  Mail,
  ShoppingCart,
  Lock,
  Loader2,
  Sun,
  Moon,
  Calendar,
  ListChecks,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { icon: Headphones, title: 'Daily Power Planning Practice Meditation', detail: 'Guided morning meditation to anchor your day from your magnetic state.' },
  { icon: Sparkles, title: '8 Emotional Set Point Meditations', detail: 'One targeted meditation for each set point — featured in the E-Book.' },
  { icon: Moon, title: '4 Bonus Meditations', detail: 'Wisdom Process, Double Bubble, Parts Integration, and Emotional Flood — for deeper integration.' },
  { icon: FileText, title: 'Daily / Weekly / Monthly Worksheets', detail: 'Printable PDFs with journal pages. Match the planner she uses in 1-on-1 sessions.' },
  { icon: ListChecks, title: 'Evening Check-In', detail: 'A 3-question reflection to close each day — what you created, noticed, and let go.' },
  { icon: Lock, title: 'Lifetime Access', detail: 'Yours forever. Log in from any device with a magic link emailed to you.' },
];

export default function PracticeLandingPage() {
  const [pkg, setPkg] = useState(null);
  const [buyEmail, setBuyEmail] = useState('');
  const [accessEmail, setAccessEmail] = useState('');
  const [buying, setBuying] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [accessSent, setAccessSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/practice/package`).then((r) => setPkg(r.data)).catch(() => {});
  }, []);

  const handleBuy = async () => {
    if (!buyEmail || !buyEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setBuying(true);
    try {
      const { data } = await axios.post(`${API}/practice/checkout`, {
        origin_url: window.location.origin,
        customer_email: buyEmail.trim().toLowerCase(),
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Checkout failed — please try again.');
      setBuying(false);
    }
  };

  const handleAccessRequest = async () => {
    if (!accessEmail || !accessEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setRequestingAccess(true);
    try {
      await axios.post(`${API}/practice/access-request`, {
        email: accessEmail.trim().toLowerCase(),
      });
      setAccessSent(true);
      toast.success('Check your inbox — magic link on the way 💛');
    } catch {
      toast.error('Could not send link. Please try again.');
    } finally {
      setRequestingAccess(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="practice-landing-root">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-silver/10">
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-secondary/15 blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 rounded-full bg-maroon/20 blur-3xl" />
        </div>
        <div className="relative container mx-auto px-4 py-20 max-w-6xl">
          <div className="grid lg:grid-cols-[1.1fr,1fr] gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="outline" className="border-secondary/30 text-secondary mb-5">
                <Sparkles className="w-3 h-3 mr-1.5 inline" /> New · Daily companion to the E-Book
              </Badge>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight mb-6" data-testid="practice-hero-title">
                Power Planning <em className="text-secondary">Practice</em> App
              </h1>
              <p className="text-base md:text-lg text-foreground/85 leading-relaxed mb-7 max-w-xl">
                Guided meditations, printable worksheets, and a 3-question evening check-in — the daily ritual that turns the E-Book into actual lived change.
              </p>

              <div className="flex flex-wrap items-baseline gap-3 mb-8">
                <span className="text-5xl font-display font-bold text-secondary" data-testid="practice-price">
                  ${pkg?.amount?.toFixed(2) || '9.99'}
                </span>
                <span className="text-lg line-through text-muted-foreground">${pkg?.original_value?.toFixed(2) || '47.00'}</span>
                <span className="text-sm text-foreground/70">· one-time · lifetime access</span>
              </div>

              <div className="max-w-md space-y-3" data-testid="practice-buy-block">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={buyEmail}
                  onChange={(e) => setBuyEmail(e.target.value)}
                  className="bg-card border-silver/30"
                  data-testid="practice-buy-email"
                />
                <Button
                  onClick={handleBuy}
                  disabled={buying}
                  size="lg"
                  className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold"
                  data-testid="practice-buy-button"
                >
                  {buying ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening checkout…</>
                  ) : (
                    <><ShoppingCart className="w-4 h-4 mr-2" /> Get Instant Access — ${pkg?.amount?.toFixed(2) || '9.99'}</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">Secure checkout via Stripe · Magic link emailed instantly</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative"
            >
              <Card className="bg-card/80 border-secondary/25 p-7 backdrop-blur">
                <div className="flex items-center gap-2 mb-4">
                  <Sun className="w-4 h-4 text-secondary" />
                  <p className="text-xs uppercase tracking-widest text-secondary font-semibold">Inside the app</p>
                </div>
                <ul className="space-y-3.5">
                  {FEATURES.map((f) => (
                    <li key={f.title} className="flex gap-3" data-testid={`practice-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}>
                      <f.icon className="w-4 h-4 mt-1 shrink-0 text-secondary" />
                      <div>
                        <p className="font-semibold text-sm text-foreground">{f.title}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{f.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EXISTING BUYER ACCESS */}
      <section className="border-b border-silver/10 bg-card/30">
        <div className="container mx-auto px-4 py-14 max-w-3xl text-center" data-testid="practice-existing-buyer-block">
          <Badge variant="outline" className="border-secondary/30 text-secondary mb-3">🎁 Free Bonus</Badge>
          <h2 className="font-heading text-2xl md:text-3xl text-foreground mb-3">Already bought the E-Book or a Collective member?</h2>
          <p className="text-foreground/75 leading-relaxed mb-6">
            The Power Planning Practice App is yours <em className="text-secondary">free</em> as a bonus. Enter the email you used to purchase and we&apos;ll send your magic link.
          </p>
          {accessSent ? (
            <div className="max-w-md mx-auto bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-6 py-5" data-testid="practice-access-sent">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-foreground">
                If you have access, a magic link is on its way to <strong>{accessEmail}</strong>.
              </p>
              <p className="text-xs text-muted-foreground mt-2">Check your inbox (and spam folder). Tap the link to open your library.</p>
            </div>
          ) : (
            <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder="email used to purchase"
                value={accessEmail}
                onChange={(e) => setAccessEmail(e.target.value)}
                className="bg-card border-silver/30"
                data-testid="practice-access-email"
              />
              <Button
                onClick={handleAccessRequest}
                disabled={requestingAccess}
                variant="outline"
                className="border-secondary/40 text-secondary hover:bg-secondary/10 whitespace-nowrap"
                data-testid="practice-access-button"
              >
                {requestingAccess ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Mail className="w-4 h-4 mr-2" /> Send my link</>)}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* WHY THIS WORKS */}
      <section className="border-b border-silver/10">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-secondary font-semibold mb-2">Why the practice matters</p>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground">Information becomes <em>integration</em> through <em>repetition</em>.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-card/60 border-silver/15 p-6">
              <Calendar className="w-5 h-5 text-secondary mb-3" />
              <h3 className="font-display text-lg text-foreground mb-2">Daily anchor</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Five minutes each morning. Your nervous system stops bracing — it starts <em>creating</em>.</p>
            </Card>
            <Card className="bg-card/60 border-silver/15 p-6">
              <FileText className="w-5 h-5 text-secondary mb-3" />
              <h3 className="font-display text-lg text-foreground mb-2">Track the shift</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Printable worksheets you actually fill out. Real ink → real pattern interrupts.</p>
            </Card>
            <Card className="bg-card/60 border-silver/15 p-6">
              <Moon className="w-5 h-5 text-secondary mb-3" />
              <h3 className="font-display text-lg text-foreground mb-2">Close each day</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">A 3-question evening check-in. Three minutes. Eight weeks. Watch what changes.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-dark to-maroon opacity-95" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(232,196,119,0.35), transparent 45%), radial-gradient(circle at 80% 50%, rgba(232,196,119,0.25), transparent 45%)',
        }} />
        <div className="relative container mx-auto px-4 py-16 max-w-3xl text-center text-primary-foreground">
          <h2 className="font-heading text-3xl md:text-4xl mb-4">
            Stop reading about it. <em className="text-secondary">Live it.</em>
          </h2>
          <p className="text-base md:text-lg opacity-90 mb-7 leading-relaxed">
            One small ritual a day. That&apos;s the difference between an idea and a transformation.
          </p>
          <Button
            onClick={() => document.querySelector('[data-testid="practice-buy-email"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            size="lg"
            className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold"
            data-testid="practice-cta-button"
          >
            Get the Practice App — $9.99
          </Button>
        </div>
      </section>
    </div>
  );
}
